import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PeopleAltOutlined,
  PersonAddAltOutlined,
  DirectionsRunOutlined,
  BusinessOutlined,
  BookOnlineOutlined,
  TodayOutlined,
  AttachMoneyOutlined,
  MonetizationOnOutlined,
  CheckroomOutlined,
  CalendarMonthOutlined,
} from "@mui/icons-material";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { getDashboardStats } from "service/admin/dashboardService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const KpiCard = ({ title, value, icon, accentColor, loading }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.alt,
        borderLeft: `4px solid ${accentColor}`,
        height: "100%",
      }}
    >
      <CardContent>
        <FlexBetween>
          <Box>
            <Typography
              variant="h6"
              color={theme.palette.secondary[300]}
              gutterBottom
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton
                variant="text"
                width={80}
                height={40}
                sx={{ bgcolor: theme.palette.primary[700] }}
              />
            ) : (
              <Typography
                variant="h3"
                fontWeight="bold"
                color={theme.palette.secondary[100]}
              >
                {value !== undefined && value !== null ? value : "-"}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: accentColor + "22",
              borderRadius: "50%",
              width: 52,
              height: 52,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: accentColor,
            }}
          >
            {icon}
          </Box>
        </FlexBetween>
      </CardContent>
    </Card>
  );
};

const SectionLabel = ({ children }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="h5"
      fontWeight="bold"
      color={theme.palette.secondary[200]}
      sx={{ mt: "1.5rem", mb: "0.75rem" }}
    >
      {children}
    </Typography>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await getDashboardStats();
      setStats(response.data.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fmt = (val) =>
    val !== undefined && val !== null
      ? typeof val === "number" && val >= 1000
        ? val.toLocaleString("en-IN")
        : val
      : "-";

  const fmtCurrency = (val) =>
    val !== undefined && val !== null
      ? `₹${Number(val).toLocaleString("en-IN")}`
      : "-";

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween>
          <Header title="DASHBOARD" subTitle="Welcome to Strikie Admin" />
        </FlexBetween>

        <SectionLabel>Users</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Total Guests"
              value={fmt(stats?.total_guests)}
              icon={<PeopleAltOutlined />}
              accentColor="#e17081"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Total Athletes"
              value={fmt(stats?.total_athletes)}
              icon={<DirectionsRunOutlined />}
              accentColor="#dc5477"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="New This Week"
              value={fmt(stats?.athletes_this_week)}
              icon={<PersonAddAltOutlined />}
              accentColor="#b74362"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="New This Month"
              value={fmt(stats?.athletes_this_month)}
              icon={<CalendarMonthOutlined />}
              accentColor="#912e4c"
              loading={loading}
            />
          </Grid>
        </Grid>

        <SectionLabel>Facilities</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Total Facilities"
              value={fmt(stats?.total_facilities)}
              icon={<BusinessOutlined />}
              accentColor="#e17081"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Bookable Facilities"
              value={fmt(stats?.bookable_facilities)}
              icon={<CheckroomOutlined />}
              accentColor="#dc5477"
              loading={loading}
            />
          </Grid>
        </Grid>

        <SectionLabel>Bookings</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Total Bookings"
              value={fmt(stats?.total_bookings)}
              icon={<BookOnlineOutlined />}
              accentColor="#e17081"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Today's Bookings"
              value={fmt(stats?.bookings_today)}
              icon={<TodayOutlined />}
              accentColor="#dc5477"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Total Revenue"
              value={fmtCurrency(stats?.revenue_total)}
              icon={<AttachMoneyOutlined />}
              accentColor="#b74362"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Today's Revenue"
              value={fmtCurrency(stats?.revenue_today)}
              icon={<MonetizationOnOutlined />}
              accentColor="#912e4c"
              loading={loading}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Dashboard;
